import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { canAccessProposalAndClientPortal, isArchitectRole } from '../../utils/userRoles';
import { useCanUseTeamChat } from '../../hooks/useCanUseTeamChat';
import Logo from '../ui/Logo';
import { LogOut, Bell, Lock, MessageSquare, UserCircle } from 'lucide-react';
import NotificationCenter from '../ui/NotificationCenter';
import FeedbackButton from '../feedback/FeedbackButton';
import CrmThemeToggle from '../ui/CrmThemeToggle';
import NotificationSettingsModal from '../settings/NotificationSettingsModal';
import ProfileSettingsModal from '../settings/ProfileSettingsModal';
import ChangePasswordPanel from './ChangePasswordPanel';
import ContractorTimesheetsHeaderLink from '../contractor/ContractorTimesheetsHeaderLink';
import '../ui/AppHeader.css';
import '../pm/pm-shared.css';
import './ArchitectHeader.css';
import UserAvatar from '../ui/UserAvatar';

type ArchitectHeaderProps = {
  homePath?: string;
  homeLinkLabel?: string;
  showStandup?: boolean;
  showHours?: boolean;
  linkSearch?: string;
};

const ArchitectHeader: React.FC<ArchitectHeaderProps> = ({
  homePath = '/designer-portal',
  homeLinkLabel = 'Design Work',
  showStandup = true,
  showHours = true,
  linkSearch = '',
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const showTeamChat = useCanUseTeamChat() && !isArchitectRole(user?.role);

  const handleLeadClick = useCallback(
    (leadId: string, tab?: 'details' | 'activities' | 'tracking') => {
      navigate(`/client/${leadId}${tab === 'activities' ? '?tab=activities' : ''}`);
    },
    [navigate],
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="app-header">
      <div className="header-left">
        <nav className="header-nav">
          <Link to={`${homePath}${linkSearch}`} className="header-brand" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            <Logo size="small" className="header-logo" />
          </Link>

          <Link
            to={`${homePath}${linkSearch}`}
            className={`nav-item ${location.pathname === homePath || location.pathname === '/architect-portal' || location.pathname === '/designer-portal' || location.pathname === '/engineer-portal' ? 'active' : ''}`}
          >
            {homeLinkLabel}
          </Link>

          {showStandup && (
            <Link
              to={`${homePath}/standup`}
              className={`nav-item ${location.pathname.endsWith('/standup') ? 'active' : ''}`}
            >
              Standup
            </Link>
          )}

          <Link
            to={`/project-hub${linkSearch}`}
            title="All projects (H)"
            aria-keyshortcuts="H"
            className={`nav-item ${location.pathname.startsWith('/project-hub') ? 'active' : ''}`}
          >
            Project Hub
          </Link>

          {showHours && <ContractorTimesheetsHeaderLink />}

          {showTeamChat && (
            <div className="nav-dropdown-container">
              <button
                type="button"
                className={`nav-item nav-dropdown-trigger ${location.pathname.startsWith('/team-chat') ? 'active' : ''}`}
              >
                People
                <svg
                  className="nav-dropdown-arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <Link
                  to="/team-chat"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/team-chat') ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <MessageSquare size={18} />
                  </span>
                  Chat
                </Link>
              </div>
            </div>
          )}
        </nav>
      </div>

      <div className="header-right">
        <FeedbackButton />
        <CrmThemeToggle />
        <NotificationCenter
          onLeadClick={canAccessProposalAndClientPortal(user?.role, user?.designations) ? handleLeadClick : undefined}
        />
        <div className="user-menu-container" ref={menuRef}>
          <button
            type="button"
            className={`user-menu-trigger ${showMenu ? 'open' : ''}`}
            onClick={() => setShowMenu(!showMenu)}
            aria-expanded={showMenu}
            aria-haspopup="true"
          >
            <UserAvatar
              className="user-avatar"
              name={user?.name || 'User'}
              photoURL={user?.photoURL}
              userId={user?.id}
            />
            <span className="user-name">{user?.name}</span>
            <svg
              className={`dropdown-arrow ${showMenu ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          {showMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-name">{user?.name}</span>
                <span className="dropdown-email">{user?.email}</span>
              </div>
              <div className="dropdown-divider" />
              {showHours && (
                <ContractorTimesheetsHeaderLink variant="menu" onNavigate={() => setShowMenu(false)} />
              )}
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowProfile(true); }}
              >
                <UserCircle size={16} />
                Profile
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowNotifPrefs(true); }}
              >
                <Bell size={16} />
                Notification Settings
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowChangePassword(true); }}
              >
                <Lock size={16} />
                Change Password
              </button>
              <button
                type="button"
                className="dropdown-item logout"
                onClick={() => { setShowMenu(false); logout(); }}
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {showNotifPrefs && (
        <NotificationSettingsModal onClose={() => setShowNotifPrefs(false)} variant="portal" />
      )}
      {showProfile && (
        <ProfileSettingsModal onClose={() => setShowProfile(false)} />
      )}
      {showChangePassword && (
        <ChangePasswordPanel onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
};

export default ArchitectHeader;
